import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

const PermissionsContext = createContext({
  permissions: null,
  loading: true,
  userRole: null,
  refresh: () => {},
  hasModulePermission: () => true,
  hasAdvancedPermission: () => true,
  canAccess: () => true
});

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(null);

  const fetchPermissions = useCallback(async () => {
    try {
      const userRes = await api.get('/auth/me');
      const user = userRes.data;
      setUserRole(user.role);
      
      if (user.role === 'admin') {
        setPermissions({ bypass: true, role: 'admin' });
        setLoading(false);
        lastFetchRef.current = Date.now();
        return;
      }
      
      const permRes = await api.get('/auth/my-permissions');
      const permData = permRes.data;
      
      if (permData.bypass) {
        setPermissions({ bypass: true, role: permData.role });
      } else {
        setRoleId(permData.role_id);
        setPermissions(permData);
      }
      
      setLoading(false);
      lastFetchRef.current = Date.now();
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setPermissions({
        module_permissions: [],
        advanced_permissions: {}
      });
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchPermissions();
    } else {
      setLoading(false);
    }
  }, [fetchPermissions]);

  // Auto-refresh every 30 seconds to pick up admin changes
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const interval = setInterval(() => {
      fetchPermissions();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchPermissions]);

  // Refresh on window focus (user switches back to tab)
  useEffect(() => {
    const onFocus = () => {
      const token = localStorage.getItem('auth_token');
      if (token && (!lastFetchRef.current || Date.now() - lastFetchRef.current > 5000)) {
        fetchPermissions();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPermissions]);

  /**
   * Check if user has specific permission for a module
   * @param {string} module - Module name (e.g., 'grey_orders', 'lr_reports')
   * @param {string} permission - Permission type (e.g., 'view', 'create', 'edit', 'delete', 'export_pdf', 'send_whatsapp')
   * @returns {boolean}
   */
  const hasModulePermission = useCallback((module, permission) => {
    // Admin and manager bypass
    if (permissions?.bypass) return true;
    if (!permissions?.module_permissions) return false;
    
    const modulePerm = permissions.module_permissions.find(m => m.module === module);
    return modulePerm ? Boolean(modulePerm[permission]) : false;
  }, [permissions]);

  /**
   * Check if user has an advanced permission
   * @param {string} permission - Permission key (e.g., 'issue_lr', 'access_admin_panel')
   * @returns {boolean}
   */
  const hasAdvancedPermission = useCallback((permission) => {
    // Admin always has all permissions
    if (permissions?.bypass) return true;
    
    if (!permissions?.advanced_permissions) return false;
    return Boolean(permissions.advanced_permissions[permission]);
  }, [permissions]);

  /**
   * Generic access check - combines module and advanced permissions
   * @param {object} config - { module: string, permission: string } or { advanced: string }
   * @returns {boolean}
   */
  const canAccess = useCallback((config) => {
    if (!config) return true;
    
    if (config.module && config.permission) {
      return hasModulePermission(config.module, config.permission);
    }
    
    if (config.advanced) {
      return hasAdvancedPermission(config.advanced);
    }
    
    return true;
  }, [hasModulePermission, hasAdvancedPermission]);

  return (
    <PermissionsContext.Provider 
      value={{ 
        permissions, 
        loading, 
        userRole,
        refresh: fetchPermissions, 
        hasModulePermission,
        hasAdvancedPermission,
        canAccess
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);

/**
 * PermissionGuard - Conditionally render children based on permissions
 * 
 * Usage:
 *   <PermissionGuard module="grey_orders" permission="create">
 *     <CreateOrderButton />
 *   </PermissionGuard>
 * 
 *   <PermissionGuard advanced="access_admin_panel">
 *     <AdminPanelLink />
 *   </PermissionGuard>
 */
export const PermissionGuard = ({ 
  module, 
  permission, 
  advanced, 
  fallback = null,
  children 
}) => {
  const { hasModulePermission, hasAdvancedPermission, loading } = usePermissions();
  
  if (loading) return null;
  
  if (module && permission) {
    if (!hasModulePermission(module, permission)) {
      return fallback;
    }
  }
  
  if (advanced) {
    if (!hasAdvancedPermission(advanced)) {
      return fallback;
    }
  }
  
  return children;
};
